// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Rediger › Geometri's ceilings and roofs (frontend-u0o.7, slice S7): a
 * room's ceiling planes (`SolidSpace.ceiling_planes`, world; its ceiling is
 * the lowest of its flat `z_ceiling` and every plane — ReUseX
 * `ceiling_at`) read and typed as a height over the floor at the room's
 * middle, a slope in degrees and the direction it rises toward; written
 * with `ceiling.set`, the flat cap raised so the planes show. Flat, shifted
 * (Snit's drag), "Tilpas til punktsky" (`POST /ceilings/fit`: its summary
 * and its apply), a gable's second half, a plane removed. Pure: no React,
 * no DOM.
 */

import type { CeilingFitResult, CeilingPlane, ModelEditOp, PlanXY, SolidSpace } from '../api/types';
import { findElement, spaceLabel, type GeometriModel } from './geometri';
import type { XY } from './planWalls';
import { ceilingAt, planeZ } from './section';
import { pointInRing } from './spaces';

/** A ceiling plane as Egenskaber types it. */
export interface PlaneSpec {
  /** Its z over the room's floor at the room's middle, metres. */
  height: number;
  /** Its slope, degrees from level. */
  deg: number;
  /** The direction it rises toward, degrees counter-clockwise from world +x. */
  dir: number;
}

/** Steepest ceiling plane (ReUseX kMaxCeilingSlopeDeg). */
export const MAX_SLOPE_DEG = 80;
/** A fit flatter than this (degrees) is set as a flat ceiling. */
export const FLAT_FIT_DEG = 1;

const r6 = (v: number) => Math.round(v * 1e6) / 1e6;
const DEG = Math.PI / 180;
const comma = (v: number, digits: number) => v.toFixed(digits).replace('.', ',');

/** The middle of a ring (its vertices' mean): where a plane's height is read. */
export function ringCentre(ring: readonly PlanXY[]): XY {
  if (ring.length === 0) return [0, 0];
  const c = ring.reduce((a, p) => [a[0] + p[0], a[1] + p[1]], [0, 0]);
  return [c[0] / ring.length, c[1] / ring.length];
}

export function planeSpec(p: CeilingPlane, centre: XY, zFloor: number): PlaneSpec {
  const [gx, gy] = p.slope ?? [0, 0];
  const g = Math.hypot(gx, gy);
  const dir = g > 1e-9 ? ((Math.atan2(gy, gx) / DEG) % 360 + 360) % 360 : 0;
  return { height: r6(planeZ(p, centre[0], centre[1]) - zFloor), deg: Math.atan(g) / DEG, dir };
}

export function planeFromSpec(spec: PlaneSpec, centre: XY, zFloor: number): CeilingPlane {
  const g = Math.tan(spec.deg * DEG);
  return { at: [r6(centre[0]), r6(centre[1]), r6(zFloor + spec.height)], slope: [r6(g * Math.cos(spec.dir * DEG)), r6(g * Math.sin(spec.dir * DEG))] };
}

/** "2,20 m · vandret", "2,20 m · hælder 5,7° · stiger mod 90°". */
export function describePlane(spec: PlaneSpec): string {
  if (spec.deg < 0.05) return `${comma(spec.height, 2)} m · vandret`;
  return `${comma(spec.height, 2)} m · hælder ${comma(spec.deg, 1)}° · stiger mod ${Math.round(spec.dir)}°`;
}

export interface CeilingView {
  guid: string;
  flat: boolean;
  /** The flat cap over the floor, metres. */
  height: number;
  centre: XY;
  zFloor: number;
  planes: (PlaneSpec & { text: string })[];
  text: string;
}

function room(model: GeometriModel, guid: string): SolidSpace | null {
  const f = findElement(model, guid);
  return f?.kind === 'space' ? f.row : null;
}

/** Egenskaber's Loft section for room `guid`. */
export function ceilingView(model: GeometriModel, guid: string): CeilingView | null {
  const sp = room(model, guid);
  if (!sp) return null;
  const centre = ringCentre(sp.polygon.outer);
  const planes = (sp.ceiling_planes ?? []).map((p) => {
    const spec = planeSpec(p, centre, sp.z_floor);
    return { ...spec, text: describePlane(spec) };
  });
  const height = r6(sp.z_ceiling - sp.z_floor);
  return {
    guid,
    flat: planes.length === 0,
    height,
    centre,
    zFloor: sp.z_floor,
    planes,
    text: planes.length === 0 ? `Fladt i ${comma(height, 2)} m` : planes.length === 1 ? 'Skråt loft' : `${planes.length} loftsplaner`,
  };
}

/** The highest the planes' ceiling reaches over the room (the cap that shows them all), sampled. */
function planesTop(sp: SolidSpace, planes: readonly CeilingPlane[]): number {
  const outer = sp.polygon.outer;
  const pts: XY[] = outer.map((p) => [p[0], p[1]]);
  const xs = outer.map((p) => p[0]);
  const ys = outer.map((p) => p[1]);
  const [x0, x1, y0, y1] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
  const n = 24;
  for (let i = 0; i <= n; i += 1)
    for (let j = 0; j <= n; j += 1) {
      const p: XY = [x0 + ((x1 - x0) * i) / n, y0 + ((y1 - y0) * j) / n];
      if (pointInRing(p[0], p[1], outer)) pts.push(p);
    }
  let top = -Infinity;
  for (const p of pts) top = Math.max(top, ceilingAt(planes, Infinity, p[0], p[1]));
  return top;
}

export type CeilingResult = { ops: ModelEditOp[]; label: string } | { error: string };

/**
 * `ceiling.set` of `planes` on room `guid`, refused for a plane steeper
 * than 80° or a ceiling under the floor at a corner (ReUseX
 * `check_ceiling_planes`); the flat cap goes up when it would hide part of
 * them.
 */
export function setPlanesOps(model: GeometriModel, guid: string, planes: readonly CeilingPlane[], label?: string): CeilingResult {
  const sp = room(model, guid);
  if (!sp) return { error: 'Vælg et rum' };
  for (const p of planes) {
    if (![...p.at, ...(p.slope ?? [0, 0])].every(Number.isFinite)) return { error: 'Skriv tal' };
    if (Math.atan(Math.hypot(...(p.slope ?? [0, 0]))) / DEG > MAX_SLOPE_DEG + 1e-9) return { error: `Et loft kan hælde højst ${MAX_SLOPE_DEG}°` };
  }
  if (sp.polygon.outer.some((v) => ceilingAt(planes, Infinity, v[0], v[1]) < sp.z_floor)) return { error: 'Loftet går ned under gulvet ved et hjørne' };
  const ops: ModelEditOp[] = [{ op: 'ceiling.set', guid, planes: planes.map((p) => ({ at: p.at, slope: p.slope ?? [0, 0] })) }];
  if (planes.length > 0) {
    const top = Math.ceil(planesTop(sp, planes) * 1000) / 1000;
    if (top > sp.z_ceiling + 5e-4) ops.push({ op: 'space.update', guid, set: { z_ceiling: top } });
  }
  return { ops, label: label ?? `Loft · ${spaceLabel(sp)}` };
}

/** Back to a flat ceiling at `z_ceiling`; null when it is flat. */
export function flatCeilingOps(model: GeometriModel, guid: string): { ops: ModelEditOp[]; label: string } | null {
  const sp = room(model, guid);
  if (!sp || (sp.ceiling_planes ?? []).length === 0) return null;
  return { ops: [{ op: 'ceiling.set', guid, planes: [] }], label: `Fladt loft · ${spaceLabel(sp)}` };
}

/** The whole ceiling `dz` metres up (Snit's drag): the flat cap and every plane. */
export function shiftCeilingOps(model: GeometriModel, guid: string, dz: number): { ops: ModelEditOp[]; label: string } | null {
  const sp = room(model, guid);
  if (!sp || !Number.isFinite(dz) || Math.abs(dz) < 5e-4) return null;
  const ops: ModelEditOp[] = [];
  const planes = sp.ceiling_planes ?? [];
  if (planes.length > 0) ops.push({ op: 'ceiling.set', guid, planes: planes.map((p) => ({ at: [p.at[0], p.at[1], r6(p.at[2] + dz)] as [number, number, number], slope: p.slope ?? [0, 0] })) });
  ops.push({ op: 'space.update', guid, set: { z_ceiling: r6(sp.z_ceiling + dz) } });
  return { ops, label: `Loftshøjde · ${spaceLabel(sp)}` };
}

/** One plane retyped (any of height, slope, direction). */
export function updatePlaneOps(model: GeometriModel, guid: string, index: number, patch: Partial<PlaneSpec>): CeilingResult | null {
  const v = ceilingView(model, guid);
  const sp = room(model, guid);
  if (!v || !sp || index < 0 || index >= v.planes.length) return null;
  const spec = { ...v.planes[index], ...patch };
  const planes = (sp.ceiling_planes ?? []).map((p, i) => (i === index ? planeFromSpec(spec, v.centre, v.zFloor) : p));
  return setPlanesOps(model, guid, planes);
}

/**
 * Tilføj plan: with none, a level plane at the flat cap; with one, its
 * mirror (the gable's other half: the same height and slope, rising the
 * other way).
 */
export function addPlaneOps(model: GeometriModel, guid: string): CeilingResult | null {
  const v = ceilingView(model, guid);
  const sp = room(model, guid);
  if (!v || !sp) return null;
  const last = v.planes[v.planes.length - 1];
  const spec: PlaneSpec = last ? { height: last.height, deg: last.deg, dir: (last.dir + 180) % 360 } : { height: v.height, deg: 0, dir: 0 };
  return setPlanesOps(model, guid, [...(sp.ceiling_planes ?? []), planeFromSpec(spec, v.centre, v.zFloor)]);
}

export function removePlaneOps(model: GeometriModel, guid: string, index: number): CeilingResult | null {
  const sp = room(model, guid);
  const planes = sp?.ceiling_planes ?? [];
  if (!sp || index < 0 || index >= planes.length) return null;
  return setPlanesOps(model, guid, planes.filter((_, i) => i !== index), `Fjern loftsplan · ${spaceLabel(sp)}`);
}

/** A fit's refusal in Danish. */
function fitReason(reason: string): string {
  const few = /too few points \((\d+)/i.exec(reason);
  if (few) return `for få punkter over rummet (${few[1]})`;
  if (/steep/i.test(reason)) return 'planet er for stejlt til et loft';
  if (/no (base )?cloud/i.test(reason)) return 'sagen har ingen punktsky';
  return reason || 'ukendt grund';
}

/** "2,47 m over gulv · hælder 3,1° · rms 1,2 cm · 3.100 af 3.402 punkter" (with `centre`: "… midt i rummet"), or why there is no fit. */
export function fitSummary(fit: CeilingFitResult, zFloor: number, centre?: XY): string {
  if (!fit.ok || !fit.plane) return `Ingen tilpasning: ${fitReason(fit.reason)}`;
  const n = (v: number) => v.toLocaleString('da-DK');
  // Its height where Egenskaber reads a plane's: over the room's middle (else at the inliers' centroid).
  const z = centre ? planeZ(fit.plane, centre[0], centre[1]) : fit.plane.at[2];
  const parts = [`${comma(z - zFloor, 2)} m over gulv${centre ? ' midt i rummet' : ''}`, `hælder ${comma(fit.slope_deg ?? 0, 1)}°`];
  if (fit.rms !== null) parts.push(`rms ${comma(fit.rms * 100, 1)} cm`);
  parts.push(`${n(fit.inliers)} af ${n(fit.points)} punkter`);
  return parts.join(' · ');
}

/** A fit whose inliers are fewer than this share of the points is doubtful. */
const FIT_MIN_SHARE = 0.25;
/** A fitted ceiling steeper than this (degrees) is doubtful in a room… */
const FIT_STEEP_DEG = 20;
/** …and so is one lower than this over the room's middle (metres over its floor). */
const FIT_LOW = 1.9;

/**
 * Why a fit may not be the room's ceiling (Danish), or null: few of the
 * points lie on it (the band caught the floor above, or a wall), or it is
 * steep and low over the middle of the room. The editor shows it with the
 * preview; Anvend stays possible.
 */
export function fitWarning(model: GeometriModel, guid: string, fit: CeilingFitResult): string | null {
  const sp = room(model, guid);
  if (!sp || !fit.ok || !fit.plane) return null;
  const c = ringCentre(sp.polygon.outer);
  const over = planeZ(fit.plane, c[0], c[1]) - sp.z_floor;
  const deg = fit.slope_deg ?? 0;
  const odd = [deg > FIT_STEEP_DEG ? `hælder ${comma(deg, 1)}°` : null, over < FIT_LOW ? `står kun ${comma(over, 2)} m over gulvet midt i rummet` : null].filter(Boolean);
  if (odd.length > 0) return `Planet ${odd.join(' og ')} — se det i snittet, før du anvender`;
  if (fit.points > 0 && fit.inliers / fit.points < FIT_MIN_SHARE) {
    return `Kun ${Math.round((100 * fit.inliers) / fit.points)} % af punkterne ligger på planet — se det i snittet, før du anvender`;
  }
  return null;
}

/**
 * Apply a fit to room `guid`: a sloped one as its one plane, one flatter
 * than a degree as a flat ceiling at the fit's height over the room's
 * middle.
 */
export function fitOps(model: GeometriModel, guid: string, fit: CeilingFitResult): CeilingResult {
  const sp = room(model, guid);
  if (!sp) return { error: 'Vælg et rum' };
  if (!fit.ok || !fit.plane) return { error: fitSummary(fit, sp.z_floor, ringCentre(sp.polygon.outer)) };
  const label = `Tilpas loft · ${spaceLabel(sp)}`;
  if ((fit.slope_deg ?? 0) < FLAT_FIT_DEG) {
    const c = ringCentre(sp.polygon.outer);
    const z = r6(Math.round(planeZ(fit.plane, c[0], c[1]) * 1000) / 1000);
    const ops: ModelEditOp[] = [];
    if ((sp.ceiling_planes ?? []).length > 0) ops.push({ op: 'ceiling.set', guid, planes: [] });
    ops.push({ op: 'space.update', guid, set: { z_ceiling: z } });
    return { ops, label };
  }
  return setPlanesOps(model, guid, [fit.plane], label);
}
